import React from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

type MarkdownLabelProps = {
  children: string;
  className?: string;
};

/**
 * Renders markdown text for form labels, supporting links, bold, italic, etc.
 * Used primarily for form field labels that need clickable hyperlinks.
 * Also supports raw HTML via rehype-raw plugin.
 */
export function MarkdownLabel({ children, className }: MarkdownLabelProps) {
  // Escape numbered list syntax so "1. Text" renders literally instead of as a list
  // This preserves question numbers in form labels
  const escapedChildren = children.replace(/^(\d+)\.\s/gm, '$1\\. ');

  return (
    <div className={className}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Links open in new tab with security attributes
          a: ({ node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline hover:text-primary-700"
            />
          ),
          // Paragraphs should not add extra margins in labels
          p: ({ node, ...props }) => <span {...props} />,
          // Strong (bold) text
          strong: ({ node, ...props }) => <strong {...props} className="font-semibold" />,
          // Emphasis (italic) text
          em: ({ node, ...props }) => <em {...props} className="italic" />,
        }}
      >
        {escapedChildren}
      </Markdown>
    </div>
  );
}

/**
 * Helper to determine if a label should be rendered as markdown or plain text/JSX
 */
export function renderLabel(label: React.ReactNode | string): React.ReactNode {
  // If it's already a React element (JSX), render as-is
  if (React.isValidElement(label)) {
    return label;
  }

  // If it's a string, render as markdown
  if (typeof label === "string") {
    return <MarkdownLabel>{label}</MarkdownLabel>;
  }

  // Fallback for other types
  return String(label);
}

/**
 * Strips leading numbers from a label (e.g., "1. Question text" -> "Question text")
 * Useful for peer responses pages where question numbers are not needed
 */
export function stripNumbersFromLabel(label: React.ReactNode | string): string {
  if (typeof label === "string") {
    // Remove leading numbers followed by period and optional space (e.g., "1. ", "1.1. ")
    return label.replace(/^\d+(\.\d+)*\.\s*/, "");
  }
  return String(label);
}

/**
 * Renders a label with leading numbers stripped, for use on response viewing pages
 */
export function renderLabelWithoutNumbers(label: React.ReactNode | string): React.ReactNode {
  return renderLabel(stripNumbersFromLabel(label));
}
